import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database, SavedReportRow } from '../database.types';
import type { SavedReportSnapshot } from '../../features/reports/saved/saved-report-snapshot';

/**
 * Saving a generated report — M10 / S10-05.
 *
 * UI → this wrapper → Postgres RPC → Storage (ARCHITECTURE §2, INV-T02).
 *
 * **RESERVE → UPLOAD → FINALIZE, and the order is the guarantee.** Saving spans
 * a database row and a Storage object, which fail independently. This is the
 * shape migrations 0006 and 0007 already established for Capture archives:
 *
 *   1. **RESERVE** — the server creates a `pending` row and derives the object
 *      path. **The client never chooses where the bytes go.**
 *   2. **UPLOAD** — the exact generated PDF, to that reserved path, with
 *      `upsert` so a retry replaces the same object rather than duplicating it.
 *   3. **FINALIZE** — the server reads `storage.objects`, requires the object
 *      to exist at exactly the reserved size, and only then marks it `ready`.
 *
 * **NOTHING REPORTS SUCCESS UNTIL `ready`.** A save that dies between steps
 * leaves a `pending` row that is invisible to the product, and Brandon still
 * holds the generated artifact and can simply try again with the same id.
 *
 * **THESE ARE THE BYTES BRANDON GENERATED.** Nothing here re-renders, re-encodes
 * or reconstructs a PDF — the artifact from S10-04 is uploaded verbatim, so the
 * document he viewed is byte-for-byte the document his client receives and the
 * one stored as history.
 *
 * **NOTHING DELETES.** There is no compensation path, deliberately: the product
 * has no way to remove a saved report or its object, and a failed save is left
 * as an unreferenced object rather than solved by giving clients the power to
 * erase durable records.
 */

export const SAVED_REPORTS_BUCKET = 'saved-reports';

export interface SaveGeneratedReportInput {
  /** Minted with the generated artifact; the durable idempotency key. */
  readonly reportId: string;
  readonly projectId: string;
  readonly reportDate: string;
  readonly pageCount: number;
  readonly filename: string;
  readonly bytes: Uint8Array;
  readonly snapshot: SavedReportSnapshot;
}

export type SaveGeneratedReportResult =
  | { readonly ok: true; readonly row: SavedReportRow }
  | {
      readonly ok: false;
      readonly stage: 'reserve' | 'upload' | 'finalize';
      readonly message: string;
    };

/** SQLSTATEs the writers raise, mapped to sentences Brandon can act on. */
const ID_IN_USE = 'CL022';
const OBJECT_MISSING = 'CL023';
const SIZE_MISMATCH = 'CL024';

const MESSAGES = {
  reserve: 'The report could not be saved. Nothing was stored — please try again.',
  upload: 'The report file could not be uploaded. Nothing was saved — please try again.',
  finalize:
    'The report was uploaded but could not be confirmed as saved. Nothing is recorded as saved — please try again.',
  idInUse: 'This report has already been saved.',
  project: 'That Project could not be found.',
} as const;

function codeOf(error: unknown): string {
  return (error as { code?: string } | null)?.code ?? '';
}

/**
 * The path the object is uploaded to.
 *
 * **DERIVED THE SAME WAY THE SERVER DERIVES IT** — `saved_report_object_path`
 * in migration 0017 — from values the server owns. It is recomputed here from
 * the RESERVED ROW rather than from the caller's input, so a client that
 * miscomputed it would upload somewhere finalize refuses to look.
 */
export function savedReportObjectPath(row: {
  owner_user_id: string;
  project_id: string;
  id: string;
}): string {
  return `${row.owner_user_id}/${row.project_id}/${row.id}.pdf`;
}

export async function saveGeneratedReport(
  client: SupabaseClient<Database>,
  input: SaveGeneratedReportInput,
): Promise<SaveGeneratedReportResult> {
  // ---- 1. RESERVE --------------------------------------------------------
  const reserved = await client.rpc('reserve_saved_report', {
    p_report_id: input.reportId,
    p_project_id: input.projectId,
    p_report_date: input.reportDate,
    p_page_count: input.pageCount,
    p_filename: input.filename,
    p_byte_size: input.bytes.byteLength,
    p_snapshot: input.snapshot,
  });

  if (reserved.error) {
    const code = codeOf(reserved.error);
    return {
      ok: false,
      stage: 'reserve',
      message:
        code === ID_IN_USE
          ? MESSAGES.idInUse
          : code === 'P0002'
            ? MESSAGES.project
            : MESSAGES.reserve,
    };
  }

  const row: SavedReportRow | null = reserved.data;
  if (!row) return { ok: false, stage: 'reserve', message: MESSAGES.reserve };

  // A reservation that is already `ready` is a completed save being retried:
  // the bytes are there and confirmed, and re-uploading them would be work
  // with no effect.
  if (row.state === 'ready') return { ok: true, row };

  // ---- 2. UPLOAD ---------------------------------------------------------
  const upload = await client.storage
    .from(SAVED_REPORTS_BUCKET)
    .upload(
      savedReportObjectPath(row),
      new Blob([input.bytes as BlobPart], { type: 'application/pdf' }),
      {
        contentType: 'application/pdf',
        // A retry REPLACES the same object — never duplicates, never orphans.
        upsert: true,
      },
    );

  if (upload.error) {
    return { ok: false, stage: 'upload', message: MESSAGES.upload };
  }

  // ---- 3. FINALIZE -------------------------------------------------------
  const finalized = await client.rpc('finalize_saved_report', { p_report_id: input.reportId });

  if (finalized.error) {
    const code = codeOf(finalized.error);
    // Both of these mean the same thing to Brandon — it is not saved — while
    // remaining distinguishable in the codebase.
    void (code === OBJECT_MISSING || code === SIZE_MISMATCH);
    return { ok: false, stage: 'finalize', message: MESSAGES.finalize };
  }

  const ready: SavedReportRow | null = finalized.data;
  if (!ready || ready.state !== 'ready') {
    return { ok: false, stage: 'finalize', message: MESSAGES.finalize };
  }

  return { ok: true, row: ready };
}
